import {
  Select,
  Tooltip,
  UnstyledButton,
  rem,
  useMantineColorScheme,
  Box,
  Center,
  Group,
  Drawer,
  Button,
  Flex,
  Text,
  Anchor,
} from '@mantine/core';
import { ReactNode, ReactElement, useState, useEffect } from 'react';
import { IconGauge, IconNotes, IconUsers, IconCalendar, IconMenu2 } from '@tabler/icons-react';
import { usePatient } from '@/contexts/PatientContext';
import classes from './EloShellSmall.module.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { ReactComponentLike } from 'prop-types';
import { PatientSelect } from '../PatientSelect/PatientSelect';

import { useTranslation } from 'react-i18next';
import { useNavbarLinks } from '../EloNavbar/useNavbarLinks';
import { LinksGroup } from '../NavbarLinksGroup/NavbarLinksGroup';
import { useMediaQuery } from '@mantine/hooks';
import logoLigth from '@/assets/logo-light.svg';
import logoDark from '@/assets/logo-dark.svg';
import { UserWidget } from '../UserWidget/UserWidget';

function TopBar(props: any) {
  return (
    <Box
      style={{
        backgroundColor: 'var(--mantine-color-body)',
        borderBottom: '1px solid var(--app-shell-border-color)',
      }}
    >
      <Center py="sm">{props.children}</Center>
    </Box>
  );
}

interface BottomBarButtonProps {
  link: string;
  label: string;
  Icon: ReactComponentLike;
  onClick?: () => void;
}

function BottomBarButton({ label, Icon, link, onClick }: BottomBarButtonProps) {
  const navigate = useNavigate();
  const [active, setActive] = useState(false);

  return (
    <Tooltip label={label} position="top" withArrow transitionProps={{ duration: 0 }} key={label}>
      <UnstyledButton
        onClick={() => {
          onClick && onClick();
          setActive(true);
          navigate(link);
        }}
        className={classes.mainLink}
        data-active={active}
      >
        <Icon style={{ width: rem(22), height: rem(22) }} stroke={1.5} />
      </UnstyledButton>
    </Tooltip>
  );
}

function BottomBar(props: any) {
  const buttons = props.children.filter((el: ReactElement) => el.type === BottomBarButton);

  return (
    <Box
      style={{
        position: 'sticky',
        bottom: '0',
        left: '0',
        right: '0',
        backgroundColor: 'var(--mantine-color-body)',
        borderTop: '1px solid var(--app-shell-border-color)',
        borderRadius: '20px 20px 0px 0px',
        padding: '10px 10px',
        boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)', // Sombra sutil para mejor apariencia
        transition: 'bottom 0.3s ease-in-out', // Transición suave
        width: '100%',
        zIndex: 50,
      }}
    >
      <Group justify="center">{buttons}</Group>
    </Box>
  );
}

export function EloShellSmall(props: any) {
  const { showSmall = true } = props;
  const { patient, patients } = usePatient();
  const { colorScheme } = useMantineColorScheme();

  const { t } = useTranslation();

  const [opened, setOpened] = useState(false);

  const linksData = useNavbarLinks();
  const location = useLocation();

  useEffect(() => {
    setOpened(false);
  }, [location.pathname]);

  const drawerLinks = linksData.map((item) => <LinksGroup {...item} key={item.label} />);

  const isTablet = useMediaQuery('(max-width: 768px) and (min-width: 480px)');

  return (
    <div
      className={classes.main}
      style={
        {
          '--app-shell-border-color':
            colorScheme === 'dark' ? 'var(--mantine-color-dark-4)' : 'var(--mantine-color-gray-3)',
          minHeight: '100vh',
        } as React.CSSProperties
      }
    >
      <Drawer
        opened={opened}
        onClose={() => setOpened(false)}
        position="left"
        withCloseButton={false}
        size={isTablet ? '50%' : '90%'}
        styles={{
          body: { display: 'flex', flexDirection: 'column', gap: '10px', minHeight: '100%' },
        }}
      >
        <Box
          style={{
            display: 'flex',
            justifyContent: 'space-between', // Distribuye los elementos en los extremos
            alignItems: 'center', // Centra verticalmente
            width: '100%', // Asegura que ocupen todo el ancho
            padding: '10px', // Espaciado opcional
          }}
        >
          <img style={{ height: '30px' }} src={colorScheme === 'dark' ? logoDark : logoLigth} />
          <UserWidget />
        </Box>

        <Box style={{ height: '80%' }}>{drawerLinks}</Box>

        <Flex
          gap="sm"
          direction="column" // Apila los elementos verticalmente
          align="center" // Centra todo
          justify="center"
          style={{ textAlign: 'center' }} // Asegura que el texto esté alineado al centro
        >
          <Anchor href="https://holaelo.com/elo-hub/privacy">{t('elo_shell_large.privacy')}</Anchor>
          <Anchor href="https://holaelo.com/elo-hub/terms-and-conditions">
            {t('elo_shell_large.terms')}
          </Anchor>
          <Text>{t('elo_shell_large.rights')}</Text>
        </Flex>
      </Drawer>
      {!showSmall
        ? null
        : patient && (
            <TopBar>
              <PatientSelect withLabel={false} />
            </TopBar>
          )}
      <div
        style={{
          flexGrow: 1,
          overflowY: 'scroll',
          overflowX: 'hidden',
        }}
      >
        <div
          style={{
            margin: 'var(--app-shell-padding)',
          }}
        >
          {props.children}
        </div>
      </div>
      {!showSmall && patients.length ? null : (
        <BottomBar>
          <BottomBarButton
            label={t('elo_shell_small.dashboard')}
            Icon={IconGauge}
            link={`/patients/${patient ? patient?.id : patients[0]?.id}/dashboard`}
          />

          <BottomBarButton
            label={t('elo_shell_small.sessions')}
            Icon={IconNotes}
            link={`/patients/${patient ? patient?.id : patients[0]?.id}/sessions`}
          />
          <BottomBarButton label="" Icon={IconMenu2} link="#" onClick={() => setOpened(true)} />
          <BottomBarButton
            label={t('elo_shell_small.patients')}
            Icon={IconUsers}
            link={`/patients`}
          />
          <BottomBarButton label={t('elo_nav_bar.booking')} Icon={IconCalendar} link={`/agenda`} />
        </BottomBar>
      )}
    </div>
  );
}
